/**
 * Scroll to a section and keep it in place while the page above it is still
 * loading (lazy images, galleries, videos, blocks that fetch their content).
 *
 * A plain scrollIntoView/scrollTo computes the target position once; when
 * content above grows afterwards, the page stops in the wrong place. This
 * re-measures for a few seconds and corrects the position, and stops as soon
 * as the visitor scrolls by hand.
 */

// Main navigation (80px) + sticky section menu (~56px) + a little breathing room
export const DEFAULT_SCROLL_OFFSET = 144;

let activeCancel: (() => void) | null = null;

const resolve = (target: string | Element | null): Element | null => {
  if (!target) return null;
  if (typeof target !== 'string') return target;
  const id = target.replace(/^#/, '');
  return document.getElementById(id) || (target.startsWith('#') ? document.querySelector(target) : null);
};

/** First of the given ids/selectors that exists on the page (and has height). */
export const findFirstSection = (candidates: string[]): Element | null => {
  let fallback: Element | null = null;
  for (const c of candidates) {
    const el = resolve(c);
    if (!el) continue;
    if (el.getBoundingClientRect().height > 0) return el;
    fallback = fallback || el;
  }
  return fallback;
};

export const scrollToSection = (
  target: string | Element | null,
  offset: number = DEFAULT_SCROLL_OFFSET,
): boolean => {
  if (!resolve(target)) return false;

  activeCancel?.();

  const targetTop = () => {
    const el = resolve(target);
    if (!el) return null;
    return Math.max(0, el.getBoundingClientRect().top + window.scrollY - offset);
  };

  const first = targetTop();
  if (first === null) return false;
  window.scrollTo({ top: first, behavior: 'smooth' });

  const started = Date.now();
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
    window.clearInterval(timer);
    window.removeEventListener('wheel', cancel);
    window.removeEventListener('touchstart', cancel);
    window.removeEventListener('keydown', cancel);
    if (activeCancel === cancel) activeCancel = null;
  };
  activeCancel = cancel;
  window.addEventListener('wheel', cancel, { passive: true });
  window.addEventListener('touchstart', cancel, { passive: true });
  window.addEventListener('keydown', cancel);

  const timer = window.setInterval(() => {
    if (cancelled) return;
    const elapsed = Date.now() - started;
    if (elapsed > 3500) return cancel();
    // Let the smooth scroll run first, then correct any drift caused by
    // content loading above the section.
    if (elapsed < 600) return;
    const top = targetTop();
    if (top === null) return cancel();
    if (Math.abs(window.scrollY - top) > 6) {
      window.scrollTo({ top, behavior: 'auto' });
    }
  }, 200);

  return true;
};
